import { type ErrorEnvelope } from '@pipemitra/shared/message-codes';

/**
 * Ticket 085 (`docs/tickets/backend/085-admin-console-api-client.md`) is in progress.
 *
 * `ApiError` stays here so existing auth tests keep importing it. New admin
 * services call the shared axios instance in `frontend/src/http/client.ts` —
 * they do not add a third client or invent envelope mapping.
 *
 * Auth may keep `apiGet` / `apiPost` until its own slice moves onto that
 * instance. Relative `/api/...` paths only (Vite proxies in development).
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly envelope: ErrorEnvelope,
  ) {
    // Never render `envelope.message` from a branch decision — branch on `messageCode`.
    // The message is localized text for a human, and it changes.
    super(envelope.messageCode);
    this.name = 'ApiError';
  }
}

export interface ApiCallOptions {
  locale?: string;
  accessToken?: string;
}

async function apiRequest<TResponse>(
  path: string,
  init: RequestInit,
  options: ApiCallOptions = {},
): Promise<TResponse> {
  const headers = new Headers(init.headers);
  headers.set('accept', 'application/json');
  headers.set('accept-language', options.locale ?? 'en');
  if (options.accessToken !== undefined && options.accessToken !== '') {
    headers.set('authorization', `Bearer ${options.accessToken}`);
  }

  const response = await fetch(path, { ...init, headers });

  if (!response.ok) {
    const envelope = (await response.json()) as ErrorEnvelope;
    throw new ApiError(response.status, envelope);
  }

  return (await response.json()) as TResponse;
}

export async function apiGet<TResponse>(
  path: string,
  locale = 'en',
  accessToken?: string,
): Promise<TResponse> {
  return await apiRequest<TResponse>(
    path,
    { method: 'GET' },
    accessToken === undefined ? { locale } : { locale, accessToken },
  );
}

export async function apiPost<TResponse>(
  path: string,
  body?: unknown,
  options: ApiCallOptions = {},
): Promise<TResponse> {
  const init: RequestInit = { method: 'POST' };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
    init.headers = { 'content-type': 'application/json' };
  }
  return await apiRequest<TResponse>(path, init, options);
}
